import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ObjectManagerComponentBase } from '../../../core/component/object-manager/object-manager.base';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { ColumnDataType, ColumnVisibility } from '../../../core/model/column-definition';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { StandardCommandHandlerResolver } from '../../../core/service/command/standard-command-handler-resolver.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { mailCommand } from '../../model/const/command';
import { Email } from '../../model/email';
import { Mail } from '../../model/mail';
import { Post } from '../../model/post';
import { Sms } from '../../model/sms';
import { MailCommandHandlerResolver } from '../../service/command/mail-command-handler-resolver.service';
import { PreviewMailCommandHandler } from '../../service/command/preview-mail-command-handler.service';
import { SendMailCommandHandler } from '../../service/command/send-mail-command-handler.service';
import { EmailService } from '../../service/data/email.service';
import { MailService } from '../../service/data/mail.service';
import { PostService } from '../../service/data/post.service';
import { SmsService } from '../../service/data/sms.service';
import { MailStatus, MailStatusToString } from '../../model/const/enums';

@Component({
    selector: 'app-letter-manager',
    templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
    styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
    providers: [MailCommandHandlerResolver, PreviewMailCommandHandler, SendMailCommandHandler],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MailManagerComponent extends PagedObjectManagerComponent<Mail> {
  constructor(
    columnFactory: ColumnDefinitionFactory,
    dispatcher: CommandDispatcher,
    handlerResolver: MailCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    private _dataService: MailService
  ) {
    super(_dataService, messageService, navigationService, dispatcher, handlerResolver);
    this.columnDefinition = [
      columnFactory.createTranslation('MAIL_LIST.STATUS_COLUMN', 'statusCaption', { allowSort: this }),
      columnFactory.createDateTime('COMMON.CREATED_DATE', 'createdDateTime'),
      columnFactory.create('MAIL_LIST.RECIPIENT_COLUMN', 'recipientName'),
      columnFactory.create('MAIL_LIST.STREET_COLUMN', 'street', { serverSideSortBinding: 'recipientStreet' }),
      columnFactory.createDate('MAIL_LIST.SEND_DATE_COLUMN', 'deliveredDateTime'),
    ];

    this.menuDefinition = [new MenuItemDefinition('MAIL_LIST.SEND_DRAFT', mailCommand.send)];

    this.addContextMenuItemDefinition('PREVIEW_BTN', mailCommand.preview);
    this.addContextMenuItemDefinition('MAIL_LIST.SEND_DRAFT_SINGLE', mailCommand.send);

    this.title = 'DASHBOARD.MENU.MAIL';
    this.preferencesKey = 'message.mail.list';

    this.allowNew = false;
    this.allowOpenOnRowClick = true;
  }

  menuDefinition: MenuItemDefinition[];

  ngOnInit() {
    super.ngOnInit();
    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe(definition => {
        this.defaultFilters = definition.filter.defaultFields;
        this.pageSize = definition.pageSize;

        this.filterDefinition = [
          new FilterDefinitionItem(
            'MAIL_LIST.STATUS_COLUMN',
            'status',
            definition.filter.status,
            [
              { caption: 'ALL_TEXT', value: '' },
              { caption: MailStatusToString(MailStatus.Created), value: MailStatus.Created.toString() },
              { caption: MailStatusToString(MailStatus.Delivered), value: MailStatus.Delivered.toString() },
              { caption: MailStatusToString(MailStatus.Invalid), value: MailStatus.Invalid.toString() },
              { caption: MailStatusToString(MailStatus.Sent), value: MailStatus.Sent.toString() },
            ],
            definition.filter.selectedFields.includes('status')
          ),
          new FilterDefinitionItemLocalDate(
            'COMMON.CREATED_DATE_FROM',
            'createdDateTime',
            definition.filter.createdDateTime,
            definition.filter.selectedFields.includes('createdDateTime')
          ),
        ];
      })
    );
  }
}

@Component({
    selector: 'app-sms-manager',
    templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
    styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SmsManagerComponent extends PagedObjectManagerComponent<Sms> {
  constructor(
    columnFactory: ColumnDefinitionFactory,
    dispatcher: CommandDispatcher,
    handlerResolver: StandardCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    private _dataService: SmsService
  ) {
    super(_dataService, messageService, navigationService, dispatcher, handlerResolver);
    this.columnDefinition = [
      columnFactory.createTranslation('COMMON.STATUS', 'statusText', { allowSort: this }),
      columnFactory.createDateTime('COMMON.CREATED_DATE', 'createdDateTime'),
      columnFactory.create('COMMON.RECEIVER', 'receiverName'),
      columnFactory.createPhoneNumber('COMMON.PHONE_NUMBER', 'phone'),
      columnFactory.create('MAIL_FORM.BODY_SMS_LABEL', 'firstPartOfMessage', { allowSort: this }),
      columnFactory.createDateTime('COMMON.SENT', 'deliveredDateTime'),
    ];

    this.title = 'DASHBOARD.MENU.SMS';
    this.preferencesKey = 'message.sms.list';
    this.allowNew = false;
    this.allowOpenOnRowClick = true;
  }

  ngOnInit() {
    super.ngOnInit();
    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe(definition => {
        this.defaultFilters = definition.filter.defaultFields;
        this.pageSize = definition.pageSize;

        this.filterDefinition = [
          new FilterDefinitionItemLocalDate(
            'COMMON.CREATED_DATE_FROM',
            'createdDateTime',
            definition.filter.createdDateTime,
            definition.filter.selectedFields.includes('createdDateTime')
          ),
          new FilterDefinitionItem(
            'COMMON.PHONE_NUMBER',
            'phone',
            definition.filter.phone,
            null,
            definition.filter.selectedFields.includes('phone')
          ),
        ];
      })
    );
  }
}

@Component({
    selector: 'app-email-manager',
    templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
    styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EmailManagerComponent extends PagedObjectManagerComponent<Email> {
  constructor(
    private _dataService: EmailService,
    columnFactory: ColumnDefinitionFactory,
    dispatcher: CommandDispatcher,
    handlerResolver: StandardCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService
  ) {
    super(_dataService, messageService, navigationService, dispatcher, handlerResolver);
    this.columnDefinition = [
      columnFactory.createDateTime('COMMON.CREATED_DATE', 'createdDateTime'),
      columnFactory.createDateTime('COMMON.SENT', 'deliveredDateTime', ColumnVisibility.optional),
      columnFactory.create('COMMON.RECEIVER', 'recipientEmailAddress'),
      columnFactory.create('MAIL_FORM.SUBJECT', 'firstPartOfSubject'),
      columnFactory.create('COMMON.EMAIL_DELIVERY_STATUS', 'deliveryStatusText', {
        serverSideSortBinding: 'DeliveryStatus',
        visibility: ColumnVisibility.default,
        helpBuilder: (e: Email) => e.deliveryInfo,
      }),
    ];
    this.title = 'DASHBOARD.MENU.EMAIL';
    this.preferencesKey = 'message.email.list';
    this.allowNew = false;
    this.allowOpenOnRowClick = true;
  }

  ngOnInit() {
    super.ngOnInit();
    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe(definition => {
        this.defaultFilters = definition.filter.defaultFields;
        this.pageSize = definition.pageSize;

        this.filterDefinition = [
          new FilterDefinitionItemLocalDate(
            'COMMON.CREATED_DATE_FROM',
            'createdDateTime',
            definition.filter.createdDateTime,
            definition.filter.selectedFields.includes('createdDateTime')
          ),
          new FilterDefinitionItem(
            'MAIL_FORM.BODY_SMS_LABEL',
            'body',
            definition.filter.body,
            null,
            definition.filter.selectedFields.includes('body')
          ),
          new FilterDefinitionItem(
            'MAIL_FORM.SUBJECT',
            'subject',
            definition.filter.subject,
            null,
            definition.filter.selectedFields.includes('subject')
          ),
          new FilterDefinitionItem(
            'MAIL_FORM.RECIPIENT_ADDRESS_HEADER',
            'recipientEmailAddress',
            definition.filter.recipientEmailAddress,
            null,
            definition.filter.selectedFields.includes('recipientEmailAddress')
          ),
        ];
      })
    );
  }
}

@Component({
    selector: 'app-post-manager',
    templateUrl: './../../../core/component/object-manager/object-manager.template.html',
    styleUrls: ['./../../../core/component/object-manager/object-manager.template.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PostManagerComponent extends ObjectManagerComponentBase<Post> {
  constructor(
    cf: ColumnDefinitionFactory,
    dispatcher: CommandDispatcher,
    handlerResolver: StandardCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    service: PostService
  ) {
    super(service, messageService, navigationService, dispatcher, handlerResolver);
    this.columnDefinition = [
      cf.create('POST.SORT_ORDER', 'sortOrder'),
      cf.create('POST.TITLE', 'title'),
      cf.createDate('POST.PUBLISH_FROM', 'publishFrom'),
      cf.createDate('POST.PUBLISH_TO', 'publishTo'),
    ];

    this.title = 'POST.LIST_HEADER';
    this.preferencesKey = 'message.post.list';
  }
}
